import { Button } from '@/components/Button';
import { Switch } from '@/components/Switch';
import { Text } from '@/components/Text';
import { LinkIcon } from '@/partials/dashboard/Cards/UpdateCard/UpdateCardLinks/LinkIcon';
import { CardLink } from '@/types/card';
import { cn } from '@/utils/cn';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { CSSProperties, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { FiLink } from 'react-icons/fi';
import { MdDragIndicator } from 'react-icons/md';

interface UpdateCardLinkItemProps {
  link: CardLink;
  onToggleActive: (id: number, isActive: boolean) => void;
  onSetDirect?: (id: number) => void;
  showDirectOption?: boolean;
  onClick?: (id: number) => void;
}

export const UpdateCardLinkListItem = memo(
  ({
    link,
    onToggleActive,
    onSetDirect,
    showDirectOption = false,
    onClick,
  }: UpdateCardLinkItemProps) => {
    const { t } = useTranslation();
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({
      id: `link-${link.id}`,
    });

    const style: CSSProperties = {
      transform: CSS.Transform.toString(transform),
      transition,
      width: '100%',
      maxWidth: '100%',
      position: 'relative',
      zIndex: isDragging ? 10 : 1,
      touchAction: 'pan-y',
    };

    const handleToggle = (checked: boolean) => {
      onToggleActive(link.id, checked);
    };

    const handleSetDirectClick = () => {
      if (onSetDirect) {
        onSetDirect(link.id);
      }
    };

    const handleItemClick = () => {
      if (onClick) {
        onClick(link.id);
      }
    };

    return (
      <div
        ref={setNodeRef}
        style={{ ...style }}
        {...attributes}
        className={cn(
          'group relative flex w-full cursor-pointer items-center gap-2 overflow-hidden rounded-xl border border-gray-100 bg-white p-4 shadow-sm',
          isDragging ? 'z-10 shadow-lg' : '',
          !link.is_active && 'opacity-70',
        )}
        onClick={handleItemClick}
      >
        {/* Drag handle icon */}
        <div
          {...listeners}
          className="flex h-12 w-8 cursor-grab touch-manipulation items-center justify-center rounded-lg bg-gray-100 active:cursor-grabbing"
          onClick={e => e.stopPropagation()}
          style={{ touchAction: 'none' }}
        >
          <MdDragIndicator size={24} className="text-gray-500" />
        </div>

        <LinkIcon
          link={link}
          iconStyle="size-14 rounded-[14px] aspect-square shadow-sm"
          emojiStyle="text-4xl size-14 rounded-[14px] aspect-square"
        />

        {/* Link content */}
        <div className="flex flex-1 flex-col overflow-hidden">
          <Text className="truncate text-lg font-medium text-gray-900">
            {link.title}
          </Text>
          <Text className="max-w-56 truncate text-sm text-gray-500">
            {link.url}
          </Text>
        </div>

        <div className="flex cursor-default gap-2">
          {!showDirectOption && (
            <div
              onClick={e => e.stopPropagation()}
              className="z-10 flex items-center justify-center"
            >
              <Switch
                checked={link.is_active}
                onChange={handleToggle}
                size="md"
              />
            </div>
          )}

          {/* Show direct link button based on conditions */}
          {link.is_direct ? (
            <div onClick={e => e.stopPropagation()} className="z-10">
              <Button
                onClick={handleSetDirectClick}
                className="flex cursor-pointer items-center gap-1 bg-linko-light px-3 py-1.5 text-xs font-medium text-white shadow-sm"
              >
                <FiLink className="size-3" />
                {t('updateCard:links.direct')}
              </Button>
            </div>
          ) : showDirectOption ? (
            <div onClick={e => e.stopPropagation()} className="z-10">
              <Button
                onClick={handleSetDirectClick}
                className="flex cursor-pointer items-center gap-1 bg-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700 shadow-sm"
              >
                <FiLink className="size-3" />
                {t('updateCard:links.make_direct')}
              </Button>
            </div>
          ) : null}
        </div>
      </div>
    );
  },
);
